import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import {
  Telegram as TelegramIcon,
  CheckCircle as CheckCircleIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  startTelegramLink,
  getTelegramLinkCode,
  getTelegramLinkStatus,
  unlinkTelegram,
} from "../../services/telegramService";

const COUNTDOWN_SECONDS = 30;
const POLL_INTERVAL_MS = 3000;

const Settings = () => {
  const authorized = useSelector((state) => state.authorized);
  const navigate = useNavigate();

  // Telegram linking state: loading | linked | idle | linking | error
  const [telegramState, setTelegramState] = useState("loading");
  const [linkCode, setLinkCode] = useState("");
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [linkedInfo, setLinkedInfo] = useState(null); // { chat_id, session_expiry }
  const [errorMessage, setErrorMessage] = useState("");

  const countdownRef = useRef(null);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearInterval(countdownRef.current);
      clearInterval(pollRef.current);
    };
  }, []);

  // Auth guard
  useEffect(() => {
    if (!authorized) {
      navigate("/login");
    }
  }, [authorized, navigate]);

  // Check initial status on mount
  useEffect(() => {
    if (authorized) {
      checkStatus();
    }
  }, [authorized]);

  const checkStatus = async () => {
    try {
      setTelegramState("loading");
      const data = await getTelegramLinkStatus();
      if (!mountedRef.current) return;
      if (data.linked) {
        setLinkedInfo({
          chat_id: data.chat_id,
          session_expiry: data.session_expiry,
        });
        setTelegramState("linked");
      } else {
        setTelegramState("idle");
      }
    } catch (err) {
      if (!mountedRef.current) return;
      if (err.message === "UNAUTHORIZED") {
        navigate("/login");
        return;
      }
      setErrorMessage("Failed to check Telegram link status.");
      setTelegramState("error");
    }
  };

  const startLinking = async () => {
    try {
      setTelegramState("linking");
      const data = await startTelegramLink();
      if (!mountedRef.current) return;
      setLinkCode(data.verification_code);
      setCountdown(data.expires_in_seconds || COUNTDOWN_SECONDS);
      startCountdown();
      startPolling();
    } catch (err) {
      if (!mountedRef.current) return;
      if (err.message === "UNAUTHORIZED") {
        navigate("/login");
        return;
      }
      setErrorMessage("Failed to start Telegram linking.");
      setTelegramState("error");
    }
  };

  const refreshCode = async () => {
    try {
      const data = await getTelegramLinkCode();
      if (!mountedRef.current) return;
      if (data.linked) {
        stopTimers();
        checkStatus();
        return;
      }
      setLinkCode(data.verification_code);
      setCountdown(data.expires_in_seconds || COUNTDOWN_SECONDS);
    } catch (err) {
      if (!mountedRef.current) return;
      if (err.message === "UNAUTHORIZED") {
        navigate("/login");
        return;
      }
      stopTimers();
      setErrorMessage("Failed to refresh linking code.");
      setTelegramState("error");
    }
  };

  const startCountdown = useCallback(() => {
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      setCountdown((prev) => {
        if (prev <= 1) {
          // Timer expired — refresh code
          refreshCode();
          return COUNTDOWN_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (!mountedRef.current) return;
      try {
        const data = await getTelegramLinkStatus();
        if (!mountedRef.current) return;
        if (data.linked) {
          stopTimers();
          setLinkedInfo({
            chat_id: data.chat_id,
            session_expiry: data.session_expiry,
          });
          setTelegramState("linked");
        }
      } catch {
        // Silently ignore poll errors — will retry on next interval
      }
    }, POLL_INTERVAL_MS);
  }, []);

  const stopTimers = () => {
    clearInterval(countdownRef.current);
    clearInterval(pollRef.current);
  };

  const handleCancel = () => {
    stopTimers();
    setLinkCode("");
    setTelegramState("idle");
  };

  const handleUnlink = async () => {
    try {
      setTelegramState("loading");
      await unlinkTelegram();
      if (!mountedRef.current) return;
      setLinkedInfo(null);
      setTelegramState("idle");
    } catch (err) {
      if (!mountedRef.current) return;
      if (err.message === "UNAUTHORIZED") {
        navigate("/login");
        return;
      }
      setErrorMessage("Failed to unlink Telegram.");
      setTelegramState("error");
    }
  };

  const maskChatId = (chatId) => {
    if (!chatId) return "***";
    const str = String(chatId);
    if (str.length <= 4) return "****";
    return str.slice(0, 2) + "****" + str.slice(-2);
  };

  const formatExpiry = (expiryStr) => {
    if (!expiryStr) return "N/A";
    try {
      return new Date(expiryStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  const countdownProgress = (countdown / COUNTDOWN_SECONDS) * 100;

  if (!authorized) return null;

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", py: 4, px: 2 }}>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, mb: 3, color: "#1a202c" }}
      >
        Settings
      </Typography>

      {/* Telegram Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px solid rgba(226,232,240,0.8)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <TelegramIcon sx={{ color: "#0088cc", fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Telegram Bot
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Link your Telegram account to receive notifications and interact with
          Datarate via our Telegram bot.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Loading */}
        {telegramState === "loading" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={36} />
          </Box>
        )}

        {/* Linked */}
        {telegramState === "linked" && linkedInfo && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                p: 2,
                borderRadius: "8px",
                backgroundColor: "rgba(16,185,129,0.08)",
                border: "1px solid rgba(16,185,129,0.2)",
              }}
            >
              <CheckCircleIcon sx={{ color: "#10b981", fontSize: 24 }} />
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#065f46" }}
              >
                Telegram linked
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Chat ID:{" "}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontFamily: "monospace", fontWeight: 600 }}
                >
                  {maskChatId(linkedInfo.chat_id)}
                </Typography>
              </Typography>
              {linkedInfo.session_expiry && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Session expires: {formatExpiry(linkedInfo.session_expiry)}
                </Typography>
              )}
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={handleUnlink}
              size="small"
            >
              Unlink Telegram
            </Button>
          </Box>
        )}

        {/* Idle */}
        {telegramState === "idle" && (
          <Button
            variant="contained"
            startIcon={<TelegramIcon />}
            onClick={startLinking}
            sx={{
              background: "linear-gradient(135deg, #0088cc 0%, #0077b5 100%)",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": {
                background:
                  "linear-gradient(135deg, #0077b5 0%, #006699 100%)",
              },
            }}
          >
            Link Telegram
          </Button>
        )}

        {/* Linking */}
        {telegramState === "linking" && (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Open our Telegram bot and send this code:
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                p: 3,
                borderRadius: "12px",
                backgroundColor: "rgba(99,102,241,0.06)",
                border: "1px solid rgba(99,102,241,0.15)",
                mb: 2,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 700,
                  letterSpacing: "0.3em",
                  color: "#4338ca",
                  userSelect: "all",
                }}
              >
                {linkCode || "-----"}
              </Typography>
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                  variant="determinate"
                  value={countdownProgress}
                  size={48}
                  thickness={4}
                  sx={{ color: "rgba(99,102,241,0.7)" }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: "#4338ca" }}
                  >
                    {countdown}s
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Code refreshes automatically when timer expires
            </Typography>
            <Button
              variant="text"
              color="inherit"
              onClick={handleCancel}
              size="small"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
          </Box>
        )}

        {/* Error */}
        {telegramState === "error" && (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage || "Something went wrong."}
            </Alert>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={checkStatus}
              size="small"
              sx={{ textTransform: "none" }}
            >
              Retry
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Settings;
